// chat/Chat.tsx — the chat panel UI for Oriora Plant Diary (cloned from Oriora Expense's
// src/chat/Chat.tsx). Purely ADDITIVE: it drives the SAME capabilities the app already has
// (add plant / log care / edit / delete / search / recap) by typing. All styling is namespaced
// under .opc-* ("Oriora Plant Chat") so it can't collide with the existing app CSS.
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit Confirm
// tap (a chat message can't destroy data on its own) — mirrors the app's ConfirmDeletePlant sheet.

import { useState, useRef, useEffect } from "react";
import { askBrain, findByName, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store, todayLocal } from "../store";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type PlantRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

const STORE_KEY = "oriora.plant.chat.v1"; // device-only; lost on clear/reinstall

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    "Tell me what to do — I'll add it, log it, find it, or recap it, no tapping.\n\n" +
    'e.g. "add plant Monstera" · "water the fern" · "story of Monstera" · "recap this week".',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A delete is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<ChatAction | null>(null);
  // A photo picked in the composer, waiting to be filed against a plant. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  /** A photo turn is filed on-device: no model call, no cost. The plant is resolved with the same
   *  fuzzy matcher the chat uses, so a misspelling still lands on the right plant. */
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const all = await store.listPlants();
      let target = null as null | { id: string; name: string };
      if (text) {
        const hits = await findByName(text);
        if (hits.length === 1) target = hits[0];
        else if (hits.length > 1) {
          setTurns([...next, { role: "assistant", content: `Which one?\n${hits.slice(0, 5).map((p) => `• ${p.name}`).join("\n")}` }]);
          return; // keep the photo attached so the next message can name the plant
        }
      }
      if (!target && all.length === 1) target = all[0];       // only one plant — no need to ask
      if (!target) {
        setTurns([...next, { role: "assistant", content: all.length ? `Which plant is this?\n${all.slice(0, 5).map((p) => `• ${p.name}`).join("\n")}` : "Add a plant first, then I'll file its photos." }]);
        return;
      }
      await store.createEvent({ plant_id: target.id, kind: "photo", done_on: todayLocal(), note: "", photo_ref: photo! });
      setPhoto(null);
      setTurns([...next, { role: "assistant", content: `📷 Added a photo of ${target.name} to its album.` }]);
      onChanged?.();
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally { setBusy(false); }
  }

  async function send() {
    const text = input.trim();
    if (busy) return;
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, action, actions } = await askBrain(next, onChunk);
      // Tier A: the brain may return SEVERAL actions ("water the fern and the monstera") — run
      // them all, in order, instead of doing the first and making the user repeat themselves.
      const queue = actions?.length ? actions : [action];
      // The conversation itself is the only thing executeAction can't reach on its own.
      const transcript = next.map((t) => `${t.role === "user" ? "You" : "Assistant"}: ${t.content}`).join("\n\n");

      const messages: string[] = [];
      let changed = false;
      let rows: PlantRow[] | undefined;

      for (let act of queue) {
        // Destructive actions NEVER auto-run from a chat turn. Anything already done stays done;
        // we stop here, report it, and hold the destructive one for an explicit Confirm tap.
        if (act.kind === "backup_restore" || act.kind === "import_file" || act.kind === "delete_plant" || act.kind === "delete_event" || act.kind === "delete_growth" || act.kind === "library_remove" || act.kind === "library_merge") {
          // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
          // resolve the priority source (what the panel's status line shows) before the hold.
          if (act.kind === "backup_restore" && !act.provider) {
            try {
              const s = await cloudBackupStatus();
              if (s.writes_to) act = { ...act, provider: s.writes_to };
            } catch { /* hold falls back to "cloud" wording */ }
          }
          setPending(act);
          const warn =
            act.kind === "import_file"
              ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
              : act.kind === "backup_restore"
              ? `Restore will REPLACE everything in the app with your ${act.provider === "gdrive" ? "Google Drive" : act.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
              : act.kind === "delete_plant"
              ? `Delete ${act.label ?? "this plant"} and its care log? This can't be undone — tap Confirm, or Cancel to keep it.`
              : act.kind === "delete_event"
              ? `Delete ${act.label ?? "that entry"}? This can't be undone — tap Confirm, or Cancel to keep it.`
              : `Delete ${act.label ?? "that measurement"}? This can't be undone — tap Confirm, or Cancel to keep it.`;
          const body = [reply, ...messages, warn].filter(Boolean).join("\n\n");
          setTurns([...next, { role: "assistant", content: body, rows }]);
          if (changed) onChanged?.();
          setBusy(false);
          return;
        }
        const result = await executeAction(act, { transcript });
        if (result.message) messages.push(result.message);
        if (result.rows) rows = result.rows;
        if (result.changed) changed = true;
      }

      const body = [reply, ...messages].filter(Boolean).join("\n\n");
      setTurns([...next, { role: "assistant", content: body || "Done.", rows }]);
      if (changed) onChanged?.();
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    try {
      const result = await executeAction(pending);
      setTurns((t) => [...t, { role: "assistant", content: result.message || "Done." }]);
      if (result.changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setPending(null);
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it. Tell me what else to do." }]);
  }

  return (
    <div className="opc-chat">
      <div className="opc-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`opc-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`opc-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <PlantRows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="opc-msgrow"><div className="opc-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {photo && !pending && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
      {pending ? (
        <div className="opc-composer opc-confirm">
          <button className="btn btn-danger" onClick={confirmPending} disabled={busy}>{pending?.kind && String(pending.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <div className="opc-composer">
          <textarea
            value={input}
            placeholder="Tell me what to do…"
            rows={2}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <MicButton value={input} onChange={setInput} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. The gate here used to be iOS-only because no other path existed. */}
            {<button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
          <PhotoButton onPhoto={setPhoto} disabled={busy} />
          <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
        </div>
      )}
    </div>
  );
}

function PlantRows({ rows }: { rows: PlantRow[] }) {
  return (
    <div className="opc-rows">
      {rows.map((r) => (
        <div key={r.id} className="opc-rowitem">
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          <span className="muted">{[r.species, r.location].filter(Boolean).join(" · ") || "—"}</span>
        </div>
      ))}
    </div>
  );
}
